import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import { PARSE_DOCUMENT_ACCEPT } from '../../../utils/documentUploadHelpers';

function SendMoneyEntryStep({
  parsingDocument,
  parsingError,
  transactionDocumentFileName,
  onFileSelect,
  onStartFromScratch,
  prefillCurrency,
}) {
  const hasPrefillCurrency = Boolean(prefillCurrency);
  const fileInputRef = useRef(null);

  const handleUploadClick = () => {
    if (!parsingDocument) {
      fileInputRef.current?.click();
    }
  };

  return (
    <div className='flex flex-col gap-[18px]'>
      <div className='rounded-[14px] border border-[var(--color-border)] bg-[var(--white)] p-7 shadow-[var(--shadow-md)]'>
        <div className='mb-[18px] flex items-start gap-3.5'>
          <div className='flex h-10 w-10 shrink-0 items-center justify-center rounded-[10px] border border-[#e5e7eb] bg-[#f5f6f8] text-[var(--n900)]'>
            <svg width='18' height='18' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='1.6' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true'>
              <path d='M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8zM14 2v6h6M16 13H8M16 17H8M10 9H8' />
            </svg>
          </div>
          <div className='min-w-0 flex-1'>
            <div className='text-base font-medium text-[var(--n900)] font-[family-name:var(--font-ui)]'>Send from invoice</div>
            <p className='mt-0.5 text-[13px] leading-snug text-[var(--text-secondary)] font-[family-name:var(--font-ui)]'>
              Upload an invoice or screenshot, we&apos;ll extract the details automatically.
              {hasPrefillCurrency
                ? ' The document can be in any currency; we will not require it to match your selected destination.'
                : ''}
            </p>
          </div>
        </div>

        <input
          ref={fileInputRef}
          type='file'
          accept={PARSE_DOCUMENT_ACCEPT}
          className='hidden'
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) onFileSelect(file);
            e.target.value = '';
          }}
        />

        <button
          type='button'
          onClick={handleUploadClick}
          disabled={parsingDocument}
          onDragOver={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
          onDrop={(e) => {
            e.preventDefault();
            e.stopPropagation();
            const file = e.dataTransfer.files?.[0];
            if (file) onFileSelect(file);
          }}
          className='flex w-full cursor-pointer flex-col items-center gap-1.5 rounded-[10px] border-[1.5px] border-dashed border-[var(--n200)] bg-[var(--off)] px-5 py-[30px] font-[family-name:var(--font-ui)] transition-colors hover:border-[var(--n400)] disabled:cursor-wait'
        >
          {parsingDocument ? (
            <>
              <div className='mb-1 h-8 w-8 animate-spin rounded-full border-2 border-[var(--n200)] border-t-[var(--n800)]' />
              <div className='text-[13.5px] font-medium text-[var(--n800)]'>Analysing with AI…</div>
            </>
          ) : (
            <>
              <svg width='22' height='22' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='1.6' strokeLinecap='round' strokeLinejoin='round' className='text-[var(--text-secondary)]' aria-hidden='true'>
                <path d='M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M17 8l-5-5-5 5M12 3v12' />
              </svg>
              <div className='text-[13.5px] font-medium text-[var(--n800)]'>
                {transactionDocumentFileName || 'Upload invoice, screenshot or PDF'}
              </div>
              <div className='text-[11.5px] text-[var(--text-tertiary)]'>JPG, PNG, PDF · click to browse</div>
            </>
          )}
        </button>

        {parsingError && (
          <p className='mt-3 text-[13px] text-[var(--color-error,#c4391c)] font-[family-name:var(--font-ui)]'>{parsingError}</p>
        )}
      </div>

      <div className='flex items-center gap-3.5'>
        <div className='h-px flex-1 bg-[var(--color-border)]' />
        <span className='text-[10px] font-semibold uppercase tracking-[0.16em] text-[var(--text-tertiary)] font-[family-name:var(--font-ui)]'>Or</span>
        <div className='h-px flex-1 bg-[var(--color-border)]' />
      </div>

      <button
        type='button'
        onClick={onStartFromScratch}
        className='flex w-full cursor-pointer items-center gap-3.5 rounded-[14px] border border-[var(--color-border)] bg-[var(--white)] p-7 text-left shadow-[var(--shadow-md)] transition-colors hover:border-[var(--n400)]'
      >
        <div className='flex h-10 w-10 shrink-0 items-center justify-center rounded-[10px] border border-[#e5e7eb] bg-[#f5f6f8] text-[var(--n900)]'>
          <svg width='18' height='18' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='1.6' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true'>
            <path d='M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z' />
          </svg>
        </div>
        <div className='min-w-0 flex-1'>
          <div className='text-[15px] font-medium text-[var(--n900)] font-[family-name:var(--font-ui)]'>
            {hasPrefillCurrency ? 'Choose recipient' : 'Send manually'}
          </div>
          <p className='mt-0.5 text-[13px] text-[var(--text-secondary)] font-[family-name:var(--font-ui)]'>
            {hasPrefillCurrency
              ? `Destination currency is already set to ${prefillCurrency}. Pick who receives the payment and confirm the amount.`
              : 'Manually enter destination, amount and recipient details.'}
          </p>
        </div>
        <svg width='18' height='18' viewBox='0 0 24 24' fill='none' stroke='var(--text-tertiary)' strokeWidth='1.6' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true'>
          <path d='M9 18l6-6-6-6' />
        </svg>
      </button>
    </div>
  );
}

SendMoneyEntryStep.propTypes = {
  parsingDocument: PropTypes.bool,
  parsingError: PropTypes.string,
  transactionDocumentFileName: PropTypes.string,
  onFileSelect: PropTypes.func.isRequired,
  onStartFromScratch: PropTypes.func.isRequired,
  prefillCurrency: PropTypes.string,
};

SendMoneyEntryStep.defaultProps = {
  parsingDocument: false,
  parsingError: '',
  transactionDocumentFileName: '',
  prefillCurrency: '',
};

export default SendMoneyEntryStep;
